import React, { useEffect, useState } from 'react';
import {
  IoArrowDownCircleOutline,
  IoArrowUpCircleOutline,
  IoClose,
  IoWalletOutline,
} from 'react-icons/io5';
import { VscLoading } from 'react-icons/vsc';

import { UserAvatar } from '../../components/UserAvatar';
import { fetchUserCostSummary } from '../../services/api';

const formatNumber = (num) => (num || 0).toLocaleString('en-US');
const formatCost = (cost) => `$${(cost || 0).toFixed(5)}`;

// Human-readable labels for the usage_type values written by
// app/config/tokens/costs.py log_llm_cost across every wired call site.
const USAGE_TYPE_LABELS = {
  task: 'Task Chats',
  workflow_node: 'Workflow Nodes',
  workflow_node_error_handling: 'Workflow Node Error Handling',
  workflow_step_chat: 'Workflow Step Chats',
  workflow_email_action: 'Workflow Email Actions',
  workflow_canvas_assist: 'Workflow Canvas Assist',
  tool_selection: 'Tool Selection',
  summarization: 'Conversation Summarization',
  agent_function_call: 'Agent Function Calls',
  search_field_inference: 'Search Field Inference',
  web_scraping: 'Web Scraping',
  knowledge_base_rag: 'Knowledge Base (RAG)',
  webhook_trigger_eval: 'Webhook Trigger Evaluation',
  unknown: 'Unattributed',
};

const usageTypeLabel = (usageType) =>
  USAGE_TYPE_LABELS[usageType] ||
  usageType
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const BreakdownRow = ({ row }) => (
  <div className="flex items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 p-3">
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-zinc-200">
        {usageTypeLabel(row.usage_type)}
      </p>
      <p className="mt-1 text-xs text-zinc-400">
        {formatNumber(row.call_count)} call{row.call_count === 1 ? '' : 's'}
      </p>
    </div>
    <div className="ml-4 flex items-center gap-4">
      <div className="flex items-center gap-3 text-xs text-zinc-400">
        <span className="flex items-center gap-1" title="Input Tokens">
          <IoArrowUpCircleOutline className="text-blue-400" />
          {formatNumber(row.input_tokens)}
        </span>
        <span className="flex items-center gap-1" title="Output Tokens">
          <IoArrowDownCircleOutline className="text-green-400" />
          {formatNumber(row.output_tokens)}
        </span>
      </div>
      <p className="w-20 text-right font-mono text-sm font-bold text-zinc-200">
        {formatCost(row.total_cost)}
      </p>
    </div>
  </div>
);

export const CostBreakdownModal = ({ isOpen, onClose, user, userIdToken }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    if (isOpen && user) {
      const load = async () => {
        setLoading(true);
        setError(null);
        try {
          const data = await fetchUserCostSummary(userIdToken, user.email);
          setSummary(data);
        } catch (err) {
          setError(err.message || 'Failed to fetch cost summary.');
        } finally {
          setLoading(false);
        }
      };
      load();
    }
  }, [isOpen, user, userIdToken]);

  if (!isOpen) return null;

  const breakdown = summary?.breakdown_by_type || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <div className="animate-fade-in-up flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-zinc-900 shadow-2xl">
        <div className="flex items-start justify-between border-b border-zinc-700/50 p-5">
          <div className="flex items-center gap-4">
            <UserAvatar user={user} className="h-14 w-14" />
            <div>
              <h2 className="text-xl font-bold text-zinc-200">{user.name}</h2>
              <p className="text-sm text-zinc-400">{user.email}</p>
              <div className="mt-2 flex items-center gap-2 text-2xl font-bold text-green-400">
                <IoWalletOutline className="text-xl" />
                {formatCost(summary?.grand_total_cost)}
                <span className="ml-1 text-sm font-normal text-zinc-400">
                  Total Cost (actual usage)
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 hover:bg-zinc-800"
          >
            <IoClose className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-zinc-950 p-4">
          {loading && (
            <div className="flex flex-col items-center justify-center p-10 text-zinc-400">
              <VscLoading className="mb-4 animate-spin text-4xl" />
              Loading cost summary...
            </div>
          )}

          {!loading && error && (
            <div className="p-10 text-center text-red-400">Error: {error}</div>
          )}

          {!loading && !error && breakdown.length === 0 && (
            <div className="p-10 text-center text-zinc-400">
              No AI usage recorded yet for this user.
            </div>
          )}

          {!loading && !error && breakdown.length > 0 && (
            <div className="space-y-2">
              {breakdown.map((row) => (
                <BreakdownRow key={row.usage_type} row={row} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
